import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { initializeHelp } from "~/redux/modules/admin/data";
import {
  setLoading,
  setSuccess,
  setError,
  resetAlert,
} from "~/redux/modules/admin/screen";
import { RootState } from "~/redux/rootReducer";
import { Help } from "~/types/help";
import { Event } from "~/types/component";
import { helpsCollection } from "~/const/firestore";

export type Module = ReturnType<typeof useModule>;

export const useModule = () => {
  const dispatch = useDispatch();

  // 画面の状態を制御
  const startLoading = () => dispatch(setLoading(true));
  const stopLoading = () => dispatch(setLoading(false));
  const resetAlertMessage = () => dispatch(resetAlert());
  const setSuccessMessage = (val: string) => dispatch(setSuccess(val));
  const setErrorMessage = (val: string) => dispatch(setError(val));

  // ヘルプデータ
  const helps = useSelector((state: RootState) => state.adminData.helps);
  const isHelpInit = useSelector(
    (state: RootState) => state.adminData.isHelpInit
  );

  const initialHelpValue: Help = {
    question: "",
    answer: "",
    created: new Date(),
  };
  const [dialogData, setDialogData] = useState<Help>(initialHelpValue);
  const changeValue = (e: Event, key: keyof Help) => {
    setDialogData({ ...dialogData, [key]: e.target.value });
  };

  // ヘルプを登録or更新
  const onClickSave = async () => {
    startLoading();
    setOpenDialog(false);
    resetAlertMessage();

    if (dialogData.docID === undefined) {
      // 新規登録
      setDialogData({ ...dialogData, created: new Date() }); // 登録日をセット

      try {
        await helpsCollection.add(dialogData);
        setTimeout(() => setSuccessMessage("ヘルプの登録をしました"), 100);
        dispatch(initializeHelp());
      } catch {
        setErrorMessage("ヘルプの登録に失敗しました");
      }
    } else {
      // 更新
      try {
        await helpsCollection
          .doc(dialogData.docID)
          .set(dialogData, { merge: true });
        setTimeout(() => setSuccessMessage("ヘルプの更新をしました"), 100);
        dispatch(initializeHelp());
      } catch {
        setErrorMessage("ヘルプの更新に失敗しました");
      }
    }

    stopLoading();
  };

  // ヘルプを削除
  const onClickDelete = async () => {
    const result = confirm(
      "本当に削除しますか？（この操作は取り消すことはできません）"
    );
    if (result) {
      startLoading();
      setOpenDialog(false);
      resetAlertMessage();

      if (dialogData.docID !== undefined) {
        try {
          await helpsCollection.doc(dialogData.docID).delete();
          dispatch(initializeHelp());
          setTimeout(() => setSuccessMessage("ヘルプの削除をしました"), 100);
        } catch {
          setErrorMessage("ヘルプの削除に失敗しました");
        }
      } else {
        setErrorMessage("ヘルプの削除に失敗しました");
      }

      stopLoading();
    }
  };

  // ダイアログ 開閉
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const handleOpenDialog = (data?: Help) => {
    if (data) {
      setDialogData(data);
    } else {
      setDialogData(initialHelpValue);
    }
    setOpenDialog(true);
  };
  const handleCloseDialog = () => setOpenDialog(false);

  // ページネーション
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: Event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return {
    states: {
      openDialog,
      dialogData,
      isHelpInit,
      helps,
      page,
      rowsPerPage,
    },
    setters: {
      handleCloseDialog,
      handleOpenDialog,
      changeValue,
      handleChangePage,
      handleChangeRowsPerPage,
    },
    actions: {
      onClickSave,
      onClickDelete,
    },
  };
};
